/**
 * Montagem e filtragem da lista de itens do menu lateral (JOR4-7365).
 *
 * Extraído sem alteração de lógica de MenuNavegacao.js e
 * MenuNavegacaoEmbarcadora.js, que mantinham cópias idênticas de
 * `generateArrayMenu`/`IncluirItem` — a do embarcador só não tinha o bloco de
 * telas internas. O redesign precisa desse código em dois lugares (a árvore
 * antiga e o menu React), então ele deixa de ser método de classe.
 *
 * A dependência de `$.each`/`$.grep` foi preservada de propósito: reescrever
 * para laços nativos aqui misturaria refatoração com o redesign e tiraria o
 * valor do passo, que é justamente "o menu continua idêntico".
 */

import ControleSessao from "../ControleSessao";

/** Linha crua devolvida por `auth/menus-allowed` (MenuAllowedDto do hera). */
export interface MenuPermitido {
    id: string;
    MNU_CODIGO: number;
    MNU_GRUPO: number;
    MNU_ORDEM: number;
    MNU_SISTEMA: string;
    MNU_TITULO: string;
    PFM_CAACODIGO: number;
    TLA_CLASSEGRID: string;
    /**
     * Nome do ícone cadastrado em `jmu_menu.jmu_icone`, padrão Font Awesome
     * (ex.: "fas fa-truck"). Vem vazio quando a tela não tem ícone cadastrado.
     */
    MNU_ICONE?: string;
}

/** Item já no formato que o dxTreeView (e o menu novo) consomem. */
export interface ItemMenu {
    ID: string;
    name: string;
    text: string;
    expanded: boolean;
    classInvoke: string;
    /** Nome do ícone vindo do cadastro. Ver MenuPermitido.MNU_ICONE. */
    icone?: string;
    /** Ausente na raiz; nos filhos é o MNU_GRUPO do pai. */
    categoria?: number;
    isGroup?: boolean;
}

export const normalizeText = (str: string): string =>
    str
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();

/**
 * Filtra os itens pelo termo digitado, mantendo os pais dos itens encontrados
 * (expandidos) para que o resultado continue navegável na árvore.
 */
export const filterMenuItems = (
    allItems: ItemMenu[],
    searchTerm: string,
): ItemMenu[] => {
    if (!searchTerm) return allItems;
    const normalized = normalizeText(searchTerm);
    const matchingIds = new Set<string>();
    const parentIds = new Set<string>();

    for (const item of allItems) {
        if (!normalizeText(item.text || "").includes(normalized)) continue;
        matchingIds.add(item.ID);
        let parentId = item.categoria;
        while (parentId != null) {
            const parent = allItems.find(
                (i) => String(i.ID) === String(parentId),
            );
            if (!parent) break;
            parentIds.add(parent.ID);
            matchingIds.add(parent.ID);
            parentId = parent.categoria;
        }
    }

    return allItems
        .filter((item) => matchingIds.has(item.ID))
        .map((item) =>
            parentIds.has(item.ID) ? { ...item, expanded: true } : item,
        );
};

/**
 * Sobe a cadeia de grupos de `codigo` até a raiz, incluindo cada nível que ainda
 * não esteja na lista. Garante que um item nunca fique órfão na árvore.
 */
const incluirItem = (
    lista: MenuPermitido[],
    codigo: number,
    menuLateral: ItemMenu[],
): void => {
    $.each(lista, (index, value) => {
        if (value.MNU_CODIGO == codigo) {
            const existeOpcao = $.grep(
                menuLateral,
                (vetor) => parseInt(vetor.ID) == codigo,
            );

            if (existeOpcao.length == 0) {
                const options: Partial<ItemMenu> = {};
                if (value.MNU_GRUPO !== 0) {
                    options.categoria = value.MNU_GRUPO;
                }
                menuLateral.push({
                    ID: value.id,
                    name: "Tela" + index,
                    text: value.MNU_TITULO,
                    expanded: false,
                    classInvoke: value.TLA_CLASSEGRID,
                    icone: value.MNU_ICONE ?? "",
                    ...options,
                });
            }
            if (value.MNU_GRUPO !== 0) {
                incluirItem(lista, value.MNU_GRUPO, menuLateral);
            }
        }
    });
};

/**
 * Acrescenta as telas que só existem para usuário interno, penduradas no grupo
 * de Cadastros. Não vêm de `menus-allowed`: são fixas no front.
 */
const incluirTelasInternas = (menuLateral: ItemMenu[]): void => {
    const grupoCadastros = menuLateral.find(
        (item) => item.text && item.text.toLowerCase().includes("cadastro"),
    );

    const baseIdItensInternos = Date.now();

    if (grupoCadastros) {
        menuLateral.push({
            ID: (baseIdItensInternos + 1).toString(),
            name: "TelaCadastroMotoristasDuplicado",
            text: "Motoristas tecnologia",
            expanded: false,
            classInvoke: "GridMotoristasTecnologia",
            isGroup: false, // Tem classe específica, não é grupo
            categoria: Number(grupoCadastros.ID),
        });
        menuLateral.push({
            ID: (baseIdItensInternos + 2).toString(),
            name: "TelaServicosJornada",
            text: "Serviços Jornada",
            expanded: false,
            classInvoke: "GridServicosJornada",
            isGroup: false,
            categoria: Number(grupoCadastros.ID),
        });
    }
};

/**
 * Converte a resposta de `menus-allowed` na lista plana do menu lateral.
 *
 * @param comTelasInternas liga o bloco de telas internas. O menu do embarcador
 * passa `false` porque nunca teve essas telas — ligá-las lá seria mudança de
 * comportamento, não redesign.
 */
export const montarItensMenu = (
    mainArray: MenuPermitido[],
    comTelasInternas: boolean,
): ItemMenu[] => {
    const menuLateral: ItemMenu[] = [];

    const mainMenus = mainArray.filter((menu) => {
        return menu.MNU_GRUPO === 0;
    });

    for (const mainMenu of mainMenus) {
        incluirItem(mainArray, mainMenu.MNU_CODIGO, menuLateral);
    }

    $.each(mainArray, (index, value) => {
        // Filtra apenas os itens que pertencem ao menu "Jornada"
        if (
            value.MNU_SISTEMA == "JOR" ||
            (value.MNU_SISTEMA == "" && value.MNU_TITULO.includes("Jornada"))
        ) {
            if (value.TLA_CLASSEGRID == "") {
                return;
            }

            const options: Partial<ItemMenu> = {};
            if (value.MNU_GRUPO !== 0) {
                incluirItem(mainArray, value.MNU_GRUPO, menuLateral);

                options.categoria = value.MNU_GRUPO;
                options.expanded = false;
            }

            menuLateral.push({
                ID: value.id,
                name: "Tela" + index,
                text: value.MNU_TITULO,
                expanded: false,
                classInvoke: value.TLA_CLASSEGRID,
                icone: value.MNU_ICONE ?? "",
                ...options,
            });
        }
    });

    if (comTelasInternas && ControleSessao.getUsuarioInterno()) {
        incluirTelasInternas(menuLateral);
    }

    return menuLateral;
};
